import { of, EMPTY, fromEvent, merge } from 'rxjs';
import {
    switchMap,
    catchError,
    tap,
    debounceTime,
    distinctUntilChanged,
    map,
} from 'rxjs/operators';
import { postJson } from './http.js';
import { renderTemplate, replaceViewContainer, showError } from './ui.js';
import { populateSubjectSelect } from './subjects.js';
import { populateSchoolGradeSelect } from './school-grades.js';
import { createTopicsWidget } from './topics-widget.js';

/**
 * Renders the similar-cards section with the given array of cards.
 * Shows the section when cards is non-empty, hides it when empty.
 */
function renderSimilarCards(similarCardsEl, listEl, cards) {
    listEl.innerHTML = '';
    if (!cards || cards.length === 0) {
        similarCardsEl.hidden = true;
        return;
    }
    for (let card of cards) {
        let row = document.createElement('div');
        row.className = 'write-columns write-columns--three';
        let frontCol = document.createElement('div');
        frontCol.className = 'write-col';
        frontCol.textContent = card.front;
        let backCol = document.createElement('div');
        backCol.className = 'write-col';
        backCol.textContent = card.back;
        let editCol = document.createElement('div');
        editCol.className = 'write-col';
        let editLink = document.createElement('a');
        editLink.href = `#edit/${card.id}`;
        editLink.textContent = 'Bearbeiten';
        editCol.appendChild(editLink);
        row.appendChild(frontCol);
        row.appendChild(backCol);
        row.appendChild(editCol);
        listEl.appendChild(row);
    }
    similarCardsEl.hidden = false;
}

/**
 * Renders the write-card view, wires the "Leeren" and "Karte hinzufügen"
 * buttons, Ctrl+Enter submission, and a central search stream that queries
 * similar cards as the user types in either textarea. Returns a merged
 * Observable of all streams. The view stays active after a successful save
 * so the user can add more cards.
 */
export function createWriteView() {
    return of(undefined).pipe(
        switchMap(() => {
            let el = renderTemplate('write-view');
            replaceViewContainer(el);

            let frontInput = el.querySelector('.card-front-input');
            let backInput = el.querySelector('.card-back-input');
            let grundschuleSelect = el.querySelector('.grundschule-select');
            let gymnasiumSelect = el.querySelector('.gymnasium-select');
            let subjectSelect = el.querySelector('.subject-select');
            let statusEl = el.querySelector('.status');
            let similarCardsEl = el.querySelector('.similar-cards');
            let similarCardsListEl = el.querySelector('.similar-cards-list');
            let addBtn = el.querySelector('.add-btn');

            populateSchoolGradeSelect(grundschuleSelect, 'grundschule');
            populateSchoolGradeSelect(gymnasiumSelect, 'gymnasium');

            let topicsWidget = createTopicsWidget(
                el.querySelector('.topics-widget-container'),
                []
            );

            let clear$ = fromEvent(
                el.querySelector('.clear-btn'),
                'click'
            ).pipe(
                tap(() => {
                    frontInput.value = '';
                    backInput.value = '';
                    grundschuleSelect.value = '';
                    gymnasiumSelect.value = '';
                    subjectSelect.value = '';
                    statusEl.textContent = '';
                    renderSimilarCards(similarCardsEl, similarCardsListEl, []);
                    topicsWidget.reset();
                })
            );

            function submitCard() {
                let front = frontInput.value.trim();
                let back = backInput.value.trim();
                if (!front || !back) {
                    statusEl.textContent =
                        'Bitte Vorderseite und Rückseite ausfüllen.';
                    return EMPTY;
                }
                let grundschuleClass = grundschuleSelect.value
                    ? parseInt(grundschuleSelect.value, 10)
                    : null;
                let gymnasiumClass = gymnasiumSelect.value
                    ? parseInt(gymnasiumSelect.value, 10)
                    : null;
                let subjectId = subjectSelect.value
                    ? parseInt(subjectSelect.value, 10)
                    : null;
                let topics = topicsWidget.getSelectedTopics().map((t) => t.id);
                statusEl.textContent = 'Wird gespeichert…';
                return postJson('/api/cards', {
                    front,
                    back,
                    grundschuleClass,
                    gymnasiumClass,
                    subjectId,
                    topics,
                }).pipe(
                    tap(() => {
                        frontInput.value = '';
                        backInput.value = '';
                        statusEl.textContent = 'Karte wurde hinzugefügt.';
                        renderSimilarCards(
                            similarCardsEl,
                            similarCardsListEl,
                            []
                        );
                        frontInput.focus();
                    }),
                    catchError((err) => {
                        showError(err);
                        return EMPTY;
                    })
                );
            }

            let add$ = fromEvent(addBtn, 'click').pipe(
                switchMap(() => submitCard())
            );

            let ctrlEnter$ = fromEvent(el, 'keydown').pipe(
                switchMap((e) =>
                    e.ctrlKey && e.key === 'Enter' ? submitCard() : EMPTY
                )
            );

            let search$ = merge(
                fromEvent(frontInput, 'input').pipe(
                    map((e) => ({
                        text: e.target.value.trim(),
                        field: 'front',
                    }))
                ),
                fromEvent(backInput, 'input').pipe(
                    map((e) => ({ text: e.target.value.trim(), field: 'back' }))
                )
            ).pipe(
                debounceTime(400),
                distinctUntilChanged(
                    (a, b) => a.field === b.field && a.text === b.text
                ),
                switchMap(({ text, field }) => {
                    if (!text) {
                        renderSimilarCards(
                            similarCardsEl,
                            similarCardsListEl,
                            []
                        );
                        return EMPTY;
                    }
                    return postJson('/api/cards/similar', { text, field }).pipe(
                        tap((cards) =>
                            renderSimilarCards(
                                similarCardsEl,
                                similarCardsListEl,
                                cards
                            )
                        ),
                        catchError(() => EMPTY)
                    );
                })
            );

            let subjects$ = populateSubjectSelect(subjectSelect);

            return merge(
                clear$,
                add$,
                ctrlEnter$,
                search$,
                subjects$,
                topicsWidget.widget$
            );
        })
    );
}
